import { useMe } from './hooks';

/**
 * Per-server dashboard rights (access model 2026-10) — frontend mirror of
 * `apps/web-api/src/lib/accessRights.ts` (keep ENTRIES in sync). The API
 * enforces the same rights on every route; this only decides what to show.
 *
 * Actions belong to the plugin, not to the page the button sits on: a Bann
 * button needs `moderation: edit` on Mitglieder and on Aktionen alike.
 */
export type GrantedLevel = 'view' | 'edit';
export type Level = 'none' | GrantedLevel;

export const ENTRY_GROUPS = [
  { key: 'overview', label: 'Übersicht' },
  { key: 'community', label: 'Community' },
  { key: 'live', label: 'Live' },
  { key: 'tools', label: 'Werkzeuge' },
  { key: 'module', label: 'Module' },
  { key: 'panels', label: 'Panels' },
  { key: 'admin', label: 'Verwaltung' },
] as const;

type GroupKey = (typeof ENTRY_GROUPS)[number]['key'];
const VE: GrantedLevel[] = ['view', 'edit'];
const V: GrantedLevel[] = ['view'];
const E: GrantedLevel[] = ['edit'];

interface EntryDef {
  key: string;
  label: string;
  group: GroupKey;
  levels: GrantedLevel[];
}
const entry = <K extends string>(key: K, label: string, group: GroupKey, levels: GrantedLevel[]) =>
  ({ key, label, group, levels }) as EntryDef & { key: K };

export const ENTRIES = [
  entry('dashboard', 'Dashboard', 'overview', V),
  entry('members', 'Mitglieder', 'community', V),
  entry('ticketList', 'Ticket-Liste', 'community', V),
  entry('leaderboard', 'XP-Rangliste', 'community', V),
  entry('moderation', 'Warnungen & Moderation', 'community', VE),
  entry('giveaways', 'Giveaways', 'community', VE),
  entry('voice', 'Voice-Belegung', 'live', V),
  entry('messageRate', 'Nachrichten-Rate', 'live', V),
  entry('voiceManager', 'Voice-Manager', 'live', E),
  entry('actions', 'Aktionen', 'tools', VE),
  entry('embed', 'Embed-Composer', 'tools', VE),
  entry('media', 'Media-Bibliothek', 'tools', VE),
  entry('music', 'Music', 'tools', VE),
  entry('nsfw', 'NSFW-Panel', 'tools', VE),
  entry('tickets', 'Tickets', 'module', VE),
  entry('welcome', 'Welcome', 'module', VE),
  entry('leave', 'Leave', 'module', VE),
  entry('leveling', 'Leveling', 'module', VE),
  entry('autoMod', 'AutoMod', 'module', VE),
  entry('logging', 'Logging', 'module', VE),
  entry('modMail', 'ModMail', 'module', VE),
  entry('tempChannels', 'Temp Channels', 'module', VE),
  entry('serverStats', 'Server-Stats', 'module', VE),
  entry('notifications', 'Notifications', 'module', VE),
  entry('patchNotes', 'Patch Notes', 'module', VE),
  entry('rules', 'Regeln', 'panels', VE),
  entry('supportRules', 'Support-Regeln', 'panels', VE),
  entry('selfRoles', 'Self-Roles', 'panels', VE),
  entry('games', 'Games-Katalog', 'panels', VE),
  entry('permabans', 'Permabans', 'admin', VE),
  entry('commandPerms', 'Command-Perms', 'admin', VE),
  entry('backups', 'Backups', 'admin', VE),
  entry('protection', 'Server-Schutz', 'admin', VE),
  entry('access', 'Zugriff verwalten', 'admin', VE),
];

export type EntryKey = (typeof ENTRIES)[number]['key'];
export type Rights = Partial<Record<EntryKey, GrantedLevel>>;

const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2 };

export const entryDef = (key: string) => ENTRIES.find((e) => e.key === key);

/** Every entry at its highest level — what admins have on every server. */
export const FULL_RIGHTS: Rights = Object.fromEntries(
  ENTRIES.map((e) => [e.key, e.levels.includes('edit') ? 'edit' : 'view'])
) as Rights;

export function levelOf(rights: Rights, key: EntryKey): Level {
  return rights[key] ?? 'none';
}

export function hasLevel(rights: Rights, key: EntryKey, needed: GrantedLevel): boolean {
  return RANK[levelOf(rights, key)] >= RANK[needed];
}

export function isSubset(a: Rights, b: Rights): boolean {
  return ENTRIES.every((e) => RANK[levelOf(a, e.key)] <= RANK[levelOf(b, e.key)]);
}

/** Rights of the logged-in user on one server (admins: everything). */
export function useGuildRights(guildId: string | undefined) {
  const me = useMe();
  const isAdmin = !!me.data?.isAdmin;
  const rights: Rights = isAdmin ? FULL_RIGHTS : (guildId && me.data?.rights?.[guildId]) || {};
  return {
    isAdmin,
    rights,
    level: (key: EntryKey) => levelOf(rights, key),
    can: (key: EntryKey, needed: GrantedLevel = 'view') => hasLevel(rights, key, needed),
  };
}
